"use client";

import React, { useState, useEffect } from "react";
import { ShieldCheck, Terminal, Cpu, Network, Lock, ChevronRight, ChevronLeft, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const consultants = [{
  name: "Julian Vane",
  role: "Managing Director",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/consultant_bw_1.png",
  certs: ["M&A Strategy", "Venture Capital", "MBA"],
  bio: "Leading the unified expansion of Eagle 47's software ventures and strategic digital infrastructure through disciplined capital allocation."
}, {
  name: "Adrian Thorne",
  role: "Infrastructure Architect",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/consultant_bw_2.png",
  certs: ["System Scale", "Enterprise Arch", "Cloud Ops"],
  bio: "Specializing in the deployment of scalable digital commerce infrastructure and high-availability systems for the enterprise portfolio."
}, {
  name: "Sarah Sterling",
  role: "Smart Contract Lead",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/consultant_bw_3.png",
  certs: ["Solidity", "Protocol Design", "LLM Integration"],
  bio: "Architecting decentralized governance and automated contracting systems to streamline cross-industry digital transactions."
}, {
  name: "David Chen",
  role: "Portfolio Strategist",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/consultant_bw_4.png",
  certs: ["Market Analysis", "Growth Ops", "Strategic Risk"],
  bio: "Driving long-term vision and operational synergy across the Quotient Technology and Cannabis Cloud ecosystems."
}, {
  name: "James Wilson",
  role: "Chief of Compliance",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1624954/img/consultant_bw_5.png",
  certs: ["JD", "Governance", "RegTech"],
  bio: "Ensuring high-authority compliance and disciplined governance across all emerging software systems and global partnerships."
}];
export default function EngineeredForExcellence() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return <section data-section-id="59309" id="operational-core" ref={ref} className="dark bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:40px_40px] opacity-[0.05]"></div>
        
        {}
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent_50%,rgba(0,0,0,0.4)_50%)] bg-[size:100%_4px] opacity-20 pointer-events-none"></div>
        
        {}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[128px] -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent/5 rounded-full blur-[128px] translate-y-1/2"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {}
        <div className={`max-w-4xl mx-auto text-center mb-20 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-mono tracking-widest uppercase mb-6">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
            </span>
            Portfolio Status: Integrated
          </div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8 text-foreground font-default">
            The Operational <span className="text-primary">Core</span>
          </h2>
          
          <p className="text-lg md:text-xl text-muted-foreground font-default leading-relaxed max-w-3xl mx-auto">
            Eagle 47 serves as the sovereign umbrella for modern digital ventures. We unify disparate software assets into a singular, high-performance ecosystem, leveraging smart-contracting precision and strategic infrastructure to ensure long-term scalability and undisputed market ownership.
          </p>
        </div>

        {}
        <div className={`transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="flex items-center justify-between mb-8 px-2">
            <h3 className="text-2xl font-semibold tracking-tight flex items-center gap-2 font-default">
              <Terminal className="w-5 h-5 text-primary" />
              Strategic Leadership
            </h3>
            <div className="h-px flex-1 bg-border mx-6 hidden sm:block opacity-50"></div>
            <div className="hidden sm:flex gap-2">
               {}
            </div>
          </div>

          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4">
              {consultants.map((consultant, index) => <CarouselItem key={index} data-index={index} className="pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                  <div className="group relative h-[450px] w-full overflow-hidden rounded-sm border border-border/50 bg-card/30 backdrop-blur-sm transition-all duration-500 hover:border-primary/50 hover:shadow-[0_0_30px_-10px_var(--color-primary)]">
                    
                    {}
                    <div className="absolute inset-0 overflow-hidden bg-black">
                      <img src={consultant.image} alt={consultant.name} className="h-full w-full object-cover object-top transition-all duration-700 grayscale contrast-125 filter group-hover:scale-105 group-hover:grayscale-0" />
                      {}
                      <div className="absolute inset-0 bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.3)_50%),linear-gradient(90deg,rgba(180,150,80,0.06),rgba(200,200,200,0.02),rgba(180,150,80,0.06))] z-10 bg-[length:100%_2px,3px_100%] pointer-events-none opacity-40 group-hover:opacity-10 transition-opacity duration-500"></div>
                    </div>

                    {}
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/95 via-black/70 to-transparent transition-opacity duration-300 group-hover:opacity-0 z-20">
                      <h4 className="text-xl font-bold text-white font-default">{consultant.name}</h4>
                      <p className="text-primary/90 font-mono text-sm uppercase tracking-wider">{consultant.role}</p>
                    </div>

                    {}
                    <div className="absolute inset-0 z-30 flex flex-col justify-end bg-black/85 p-6 opacity-0 backdrop-blur-md transition-all duration-300 group-hover:opacity-100">
                      <div className="transform translate-y-4 transition-transform duration-300 group-hover:translate-y-0">
                        <h4 className="text-xl font-bold text-white mb-1 font-default">{consultant.name}</h4>
                        <p className="text-primary font-mono text-sm mb-4 uppercase tracking-wider">{consultant.role}</p>
                        
                        <p className="text-sm text-slate-300 mb-4 line-clamp-4 font-default">
                          {consultant.bio}
                        </p>

                        <div className="space-y-2">
                          <p className="text-xs text-muted-foreground uppercase tracking-widest font-semibold font-mono">Expertise</p>
                          <div className="flex flex-wrap gap-2">
                            {consultant.certs.map((cert, i) => <Badge key={i} variant="outline" className="bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 transition-colors rounded-none px-2 py-0 text-[10px] font-mono">
                                {cert}
                              </Badge>)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {}
                    <div className="absolute top-3 right-3 h-2 w-2 border-t border-r border-primary/50 opacity-0 transition-opacity group-hover:opacity-100 z-40"></div>
                    <div className="absolute bottom-3 left-3 h-2 w-2 border-b border-l border-primary/50 opacity-0 transition-opacity group-hover:opacity-100 z-40"></div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="flex justify-end gap-2 mt-8 pr-4">
              <CarouselPrevious className="static translate-y-0 bg-secondary/50 border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors rounded-none" />
              <CarouselNext className="static translate-y-0 bg-secondary/50 border-border text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors rounded-none" />
            </div>
          </Carousel>
        </div>

        {}
        <div className={`mt-16 text-center transition-all duration-1000 delay-500 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Link to="#">
            <Button size="lg" className="group bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)] border-none rounded-none px-8">
              Explore Our Strategy
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}